class SaMobileMenuDrawer extends HTMLElement {
  static PANEL_TRANSITION_MS = 300;

  constructor() {
    super();
    this.panelStack = ['root'];
    this.panelTransitionTimer = null;
    this.closeAnimationTimer = null;
    this.accordionAnimations = new WeakMap();
    this.onDocumentKeyup = this.onDocumentKeyup.bind(this);
    this.onResize = this.onResize.bind(this);
  }

  connectedCallback() {
    this.mainDetails = this.querySelector('details');
    this.viewport = this.querySelector('[data-sa-menu-viewport]');
    this.panels = Array.from(this.querySelectorAll('[data-sa-menu-panel]'));
    this.summary = this.querySelector('summary');
    this.overlay = this.querySelector('[data-sa-menu-overlay]');

    if (!this.mainDetails || !this.viewport) return;

    this.panels.forEach((panel) => panel.removeAttribute('hidden'));
    this.syncPanels(false);
    this.mainDetails.addEventListener('toggle', this.onMainToggle.bind(this));
    this.summary?.addEventListener('click', this.onSummaryClick.bind(this));

    this.addEventListener('click', (event) => {
      const openTrigger = event.target.closest('[data-sa-menu-open]');
      if (openTrigger) {
        event.preventDefault();
        this.openPanel(openTrigger.dataset.saMenuOpen);
        return;
      }

      const backTrigger = event.target.closest('[data-sa-menu-back]');
      if (backTrigger) {
        event.preventDefault();
        this.goBack();
        return;
      }

      const closeTrigger = event.target.closest('[data-sa-menu-close]');
      if (closeTrigger) {
        event.preventDefault();
        this.closeDrawer();
        return;
      }

      if (event.target === this.overlay) {
        this.closeDrawer();
      }
    });

    this.bindAccordions();
  }

  onSummaryClick(event) {
    if (!this.mainDetails.hasAttribute('open')) return;

    event.preventDefault();
    this.closeDrawer();
  }

  onMainToggle() {
    if (this.mainDetails.open) {
      this.cancelCloseAnimation();

      requestAnimationFrame(() => {
        this.mainDetails.classList.add('menu-opening');
        this.syncPanels(false);
        document.body.classList.add('overflow-hidden-tablet');
        document.addEventListener('keyup', this.onDocumentKeyup);
        window.addEventListener('resize', this.onResize);
        trapFocus(this.mainDetails, this.summary);
      });
    } else {
      this.mainDetails.classList.remove('menu-opening');
      document.body.classList.remove('overflow-hidden-tablet');
      document.removeEventListener('keyup', this.onDocumentKeyup);
      window.removeEventListener('resize', this.onResize);
      this.syncPanels(false);
      removeTrapFocus(this.summary);
    }
  }

  onDocumentKeyup(event) {
    if (event.code.toUpperCase() === 'ESCAPE') {
      if (this.panelStack.length > 1) {
        this.goBack();
      } else {
        this.closeDrawer();
      }
    }
  }

  onResize() {
    if (window.matchMedia('(min-width: 1000px)').matches) {
      this.closeDrawer(false, false);
    }
  }

  cancelCloseAnimation() {
    if (!this.closeAnimationTimer) return;
    clearTimeout(this.closeAnimationTimer);
    this.closeAnimationTimer = null;
  }

  closeAnimation() {
    this.cancelCloseAnimation();

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const transitionMs = reducedMotion ? 0 : SaMobileMenuDrawer.PANEL_TRANSITION_MS;

    if (transitionMs === 0) {
      this.mainDetails.removeAttribute('open');
      return;
    }

    this.closeAnimationTimer = window.setTimeout(() => {
      this.closeAnimationTimer = null;
      if (!this.mainDetails.classList.contains('menu-opening')) {
        this.mainDetails.removeAttribute('open');
      }
    }, transitionMs);
  }

  getPanel(panelId) {
    return this.panels.find((panel) => panel.dataset.saMenuPanel === panelId);
  }

  cancelPanelTransition() {
    if (!this.panelTransitionTimer) return;
    clearTimeout(this.panelTransitionTimer);
    this.panelTransitionTimer = null;
    delete this.viewport.dataset.navDirection;
  }

  clearPanelMotionClasses(panel) {
    if (!panel) return;
    panel.classList.remove(
      'is-active',
      'is-leaving',
      'is-pre-enter',
      'is-pre-enter-back',
      'is-parked-left',
      'is-parked-right',
    );
  }

  setPanelParked(panel, direction) {
    if (!panel) return;
    this.clearPanelMotionClasses(panel);
    panel.classList.add(direction === 'left' ? 'is-parked-left' : 'is-parked-right');
    panel.setAttribute('aria-hidden', 'true');
    panel.inert = true;
  }

  commitPanelTransition(leavingPanel, enteringPanel, direction) {
    this.setPanelParked(leavingPanel, direction === 'forward' ? 'left' : 'right');

    if (enteringPanel) {
      this.clearPanelMotionClasses(enteringPanel);
      enteringPanel.classList.add('is-active');
      enteringPanel.setAttribute('aria-hidden', 'false');
      enteringPanel.inert = false;
    }

    delete this.viewport.dataset.navDirection;
    this.panelTransitionTimer = null;
  }

  animatePanelChange({ direction, leavingPanel, enteringPanel, onComplete }) {
    this.cancelPanelTransition();

    this.clearPanelMotionClasses(leavingPanel);
    this.clearPanelMotionClasses(enteringPanel);

    if (direction === 'forward') {
      enteringPanel?.classList.add('is-pre-enter');
    } else {
      enteringPanel?.classList.add('is-pre-enter-back');
    }

    if (enteringPanel) {
      enteringPanel.setAttribute('aria-hidden', 'false');
      enteringPanel.inert = false;
    }

    if (leavingPanel) {
      leavingPanel.classList.add('is-active');
    }

    enteringPanel?.getBoundingClientRect();

    this.viewport.dataset.navDirection = direction;
    leavingPanel?.classList.add('is-leaving');

    requestAnimationFrame(() => {
      enteringPanel?.classList.remove('is-pre-enter', 'is-pre-enter-back');
      enteringPanel?.classList.add('is-active');
      leavingPanel?.classList.remove('is-active');
    });

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      this.commitPanelTransition(leavingPanel, enteringPanel, direction);
      onComplete?.();
      return;
    }

    this.panelTransitionTimer = window.setTimeout(() => {
      this.commitPanelTransition(leavingPanel, enteringPanel, direction);
      onComplete?.();
    }, SaMobileMenuDrawer.PANEL_TRANSITION_MS);
  }

  openPanel(panelId) {
    const currentId = this.panelStack[this.panelStack.length - 1];
    if (!panelId || currentId === panelId) return;

    const currentPanel = this.getPanel(currentId);
    const nextPanel = this.getPanel(panelId);
    if (!nextPanel) return;

    this.panelStack.push(panelId);

    this.animatePanelChange({
      direction: 'forward',
      leavingPanel: currentPanel,
      enteringPanel: nextPanel,
    });

    this.updateNavActiveState(panelId);
  }

  goBack() {
    if (this.panelStack.length <= 1) return;

    const currentId = this.panelStack[this.panelStack.length - 1];
    const currentPanel = this.getPanel(currentId);

    this.panelStack.pop();

    const previousId = this.panelStack[this.panelStack.length - 1];
    const previousPanel = this.getPanel(previousId);
    if (!previousPanel) return;

    this.animatePanelChange({
      direction: 'back',
      leavingPanel: currentPanel,
      enteringPanel: previousPanel,
    });

    this.updateNavActiveState(previousId);
  }

  syncPanels(animate = true) {
    this.cancelPanelTransition();
    this.panelStack = ['root'];

    this.panels.forEach((panel) => {
      const isRoot = panel.dataset.saMenuPanel === 'root';
      this.clearPanelMotionClasses(panel);

      if (isRoot) {
        panel.classList.add('is-active');
        panel.setAttribute('aria-hidden', 'false');
        panel.inert = false;
      } else {
        panel.classList.add('is-parked-right');
        panel.setAttribute('aria-hidden', 'true');
        panel.inert = true;
      }
    });

    if (!animate) {
      this.viewport.classList.add('sa-mobile-menu-drawer__viewport--no-transition');
      requestAnimationFrame(() => {
        this.viewport.classList.remove('sa-mobile-menu-drawer__viewport--no-transition');
      });
    }

    this.updateNavActiveState('root');
    this.closeAllAccordions();
  }

  resetPanels() {
    this.syncPanels(false);
  }

  bindAccordions(root = this) {
    root.querySelectorAll('[data-sa-menu-accordion]').forEach((accordion) => {
      if (accordion.dataset.saAccordionBound === 'true') return;
      accordion.dataset.saAccordionBound = 'true';

      const summary = accordion.querySelector(':scope > summary');
      const content = accordion.querySelector(':scope > .sa-mobile-menu-drawer__accordion-content');

      const syncState = () => {
        const isOpen = accordion.open;
        accordion.classList.toggle('is-open', isOpen);
        summary?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        content?.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
        if (content) {
          content.inert = !isOpen;
          content.style.height = isOpen ? 'auto' : '0px';
          content.style.opacity = isOpen ? '1' : '0';
        }
      };

      summary?.addEventListener('click', (event) => {
        event.preventDefault();
        const shouldOpen = !accordion.classList.contains('is-open');

        if (shouldOpen) {
          const group = accordion.closest('li')?.parentElement;
          group?.querySelectorAll(':scope > li > details[data-sa-menu-accordion][open]').forEach((sibling) => {
            if (sibling !== accordion) this.setAccordionState(sibling, false);
          });
        }

        this.setAccordionState(accordion, shouldOpen);
      });

      syncState();
    });
  }

  setAccordionState(accordion, shouldOpen, animate = true) {
    const summary = accordion.querySelector(':scope > summary');
    const content = accordion.querySelector(':scope > .sa-mobile-menu-drawer__accordion-content');
    if (!content) return;

    const currentHeight = content.getBoundingClientRect().height;
    const currentOpacity = Number.parseFloat(getComputedStyle(content).opacity) || 0;
    this.accordionAnimations.get(accordion)?.cancel();

    if (shouldOpen) {
      accordion.open = true;
      accordion.classList.add('is-open');
      content.inert = false;
    } else {
      accordion.classList.remove('is-open');
    }

    summary?.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
    content.setAttribute('aria-hidden', shouldOpen ? 'false' : 'true');

    const targetHeight = shouldOpen ? content.scrollHeight : 0;
    const targetOpacity = shouldOpen ? 1 : 0;

    if (
      !animate ||
      typeof content.animate !== 'function' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      content.style.height = shouldOpen ? 'auto' : '0px';
      content.style.opacity = `${targetOpacity}`;
      accordion.open = shouldOpen;
      content.inert = !shouldOpen;
      this.accordionAnimations.delete(accordion);
      return;
    }

    content.style.height = `${targetHeight}px`;
    content.style.opacity = `${targetOpacity}`;

    const animation = content.animate(
      [
        { height: `${currentHeight}px`, opacity: currentOpacity },
        { height: `${targetHeight}px`, opacity: targetOpacity },
      ],
      {
        duration: SaMobileMenuDrawer.PANEL_TRANSITION_MS,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
      },
    );

    this.accordionAnimations.set(accordion, animation);
    animation.finished
      .then(() => {
        if (this.accordionAnimations.get(accordion) !== animation) return;
        this.accordionAnimations.delete(accordion);
        content.style.height = shouldOpen ? 'auto' : '0px';
        content.style.opacity = `${targetOpacity}`;
        accordion.open = shouldOpen;
        content.inert = !shouldOpen;
      })
      .catch(() => {});
  }

  closeAllAccordions() {
    this.querySelectorAll('details[data-sa-menu-accordion][open]').forEach((accordion) => {
      this.setAccordionState(accordion, false, false);
    });
  }

  updateViewport(animate = true) {
    this.panels = Array.from(this.querySelectorAll('[data-sa-menu-panel]'));
    if (!animate) {
      this.syncPanels(false);
    }
  }

  updateNavActiveState(activePanelId) {
    this.querySelectorAll('[data-sa-menu-open]').forEach((trigger) => {
      const isActive = trigger.dataset.saMenuOpen === activePanelId;
      trigger.classList.toggle('is-active', isActive);
      trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    });
  }

  closeDrawer(focusSummary = true, animate = true) {
    if (!this.mainDetails.open) return;

    this.syncPanels(false);
    this.mainDetails.classList.remove('menu-opening');
    document.body.classList.remove('overflow-hidden-tablet');
    document.removeEventListener('keyup', this.onDocumentKeyup);
    window.removeEventListener('resize', this.onResize);

    if (focusSummary) {
      this.summary.setAttribute('aria-expanded', 'false');
      removeTrapFocus(this.summary);
    }

    if (animate) {
      this.closeAnimation();
      return;
    }

    this.cancelCloseAnimation();
    this.mainDetails.removeAttribute('open');
  }
}

customElements.define('sa-mobile-menu-drawer', SaMobileMenuDrawer);
